import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin, Clock, Gift, Navigation } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, ADDRESS, MAPS_URL, IMAGES } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";

const APPLE_MAPS_URL = "https://maps.apple.com/?daddr=1399%20Kipling%20St%2C%20Lakewood%2C%20CO%2080215";
const MAP_EMBED = "https://www.google.com/maps?q=Elite+Spa+1399+Kipling+St+Lakewood+CO+80215&output=embed";

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  const details = [
    { icon: MapPin, label: c.visit, value: ADDRESS, href: MAPS_URL, testid: "contact-address" },
    { icon: Phone, label: c.call, value: PHONE_DISPLAY, href: PHONE_TEL, testid: "contact-phone" },
    { icon: Clock, label: c.hours, value: c.hoursValue, testid: "contact-hours" },
    { icon: Gift, label: c.giftCerts, value: c.available, testid: "contact-gifts" },
  ];

  return (
    <section ref={ref} id="visit" data-testid="contact-section" className="relative py-28 lg:py-44 overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24 -bottom-24">
        <img src={IMAGES.sauna} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050706] via-[#050706]/70 to-[#050706]" />
      </motion.div>
      <span aria-hidden="true" className="ghost-char top-10 right-2">迎</span>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-[#c5a059]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{c.label}</span>
          </div>
          <h2 className="font-serif-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#f0f4f2] max-w-3xl">
            {c.title.pre}<span className="italic gold-shimmer-text">{c.title.it}</span>{c.title.post}
          </h2>
          <p className="mt-8 text-[#8a9690] font-light max-w-xl leading-relaxed">{c.body}</p>
        </motion.div>

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 self-start">
            {details.map((d, i) => {
              const Inner = (
                <>
                  <d.icon size={18} className="text-[#c5a059]" />
                  <span className="mt-5 block text-[10px] tracking-[0.3em] uppercase text-[#8a9690]">{d.label}</span>
                  <span className="mt-2 block font-serif-display text-xl text-[#f0f4f2] leading-snug">{d.value}</span>
                </>
              );
              const cls = "block h-full bg-[#050706]/80 backdrop-blur-sm p-8 hover:bg-[#0c100e] transition-colors duration-500";
              return (
                <motion.div
                  key={d.testid}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {d.href ? (
                    <a href={d.href} target={d.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={d.testid} className={cls}>
                      {Inner}
                    </a>
                  ) : (
                    <div data-testid={d.testid} className={cls}>{Inner}</div>
                  )}
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div data-testid="contact-map" className="relative border border-white/10 overflow-hidden group">
              <iframe
                title="Elite SPA location map"
                src={MAP_EMBED}
                className="w-full h-[300px] border-0 spa-map"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-map-overlay"
                aria-label={c.mapHint}
                className="absolute inset-x-0 top-0 h-10 flex items-center justify-center gap-2 bg-[#050706]/80 backdrop-blur-sm text-[10px] tracking-[0.25em] uppercase text-[#c5a059]"
              >
                <Navigation size={12} /> {c.mapHint}
              </a>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-t-0 border-white/10">
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-google-maps"
                className="flex items-center justify-center gap-2 bg-[#050706]/90 hover:bg-[#0c100e] text-[#f0f4f2] hover:text-[#c5a059] py-4 text-[10px] tracking-[0.2em] uppercase transition-colors duration-300"
              >
                {c.googleMaps}
              </a>
              <a
                href={APPLE_MAPS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-apple-maps"
                className="flex items-center justify-center gap-2 bg-[#050706]/90 hover:bg-[#0c100e] text-[#f0f4f2] hover:text-[#c5a059] py-4 text-[10px] tracking-[0.2em] uppercase transition-colors duration-300"
              >
                {c.appleMaps}
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-14 flex flex-wrap items-center gap-5"
        >
          <a
            href={PHONE_TEL}
            data-testid="contact-call-button"
            className="group relative overflow-hidden bg-[#c5a059] text-[#050706] px-10 py-5 text-xs tracking-[0.2em] uppercase font-medium transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="relative z-10 flex items-center gap-3">
              <Phone size={14} /> {c.callNow}
            </span>
            <span className="absolute inset-0 bg-[#dcc286] translate-y-full group-hover:translate-y-0 transition-transform duration-400" />
          </a>
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-directions-button"
            className="border border-white/25 text-[#f0f4f2] hover:border-[#c5a059] hover:text-[#c5a059] px-10 py-5 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
          >
            {c.directions}
          </a>
        </motion.div>

        <p className="mt-10 text-xs text-[#8a9690]/70 font-light max-w-md leading-relaxed">{c.note}</p>
      </div>
    </section>
  );
};
